import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { useUserStore } from '../userStore';
import { useLeagueStore } from '../leagueStore';
import type { LeagueModeContext, LeagueType } from '../types/leagueMode';

/**
 * Read-only hook that returns the current league's mode and build-phase status.
 * Safe to call in any component — returns isLoading=true while fetching.
 */
export function useLeagueMode(): LeagueModeContext {
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const currentUserId = useUserStore((state) => state.user_id);

  const [leagueType, setLeagueType] = useState<LeagueType | null>(null);
  const [initialBuildComplete, setInitialBuildComplete] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!selectedLeague?.id || !currentUserId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchMode = async () => {
      setIsLoading(true);

      try {
        // Fetch league type
        const { data: leagueData, error: leagueError } = await supabase
          .from('leagues')
          .select('league_type')
          .eq('league_id', selectedLeague.id)
          .single();

        if (leagueError && leagueError.code !== 'PGRST116') {
          console.error('[useLeagueMode] Error fetching league type:', leagueError);
        }

        // Fetch build completion status
        const { data: userLeagueData, error: userLeagueError } = await supabase
          .from('leagues_users')
          .select('initial_build_complete')
          .eq('league_id', selectedLeague.id)
          .eq('user_id', currentUserId)
          .single();

        if (userLeagueError && userLeagueError.code !== 'PGRST116') {
          console.error('[useLeagueMode] Error fetching build status:', userLeagueError);
        }

        if (!cancelled) {
          const type = (leagueData?.league_type as LeagueType | null) ?? 'dynasty';
          setLeagueType(type);
          setInitialBuildComplete(userLeagueData?.initial_build_complete ?? false);
        }
      } catch (err) {
        console.error('[useLeagueMode] Unexpected error:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchMode();
    return () => { cancelled = true; };
  }, [selectedLeague?.id, currentUserId]);

  const isStandardFlexible = leagueType === 'standard_flexible';
  const isDynasty = !isStandardFlexible; // dynasty is the default

  return {
    leagueType,
    isDynasty,
    isStandardFlexible,
    isBuildPhase: isStandardFlexible && !initialBuildComplete,
    isLoading,
  };
}
